import { NavBar } from "@/components/NavBar";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { BuscarSorteioVigenteDto } from "@/dtos/BuscarSorteioVigenteDto"
import { PremioDto } from "@/dtos/PremioDto";
import SorteioService from "@/services/SorteioService";
import { Trophy, Monitor } from "lucide-react";
import { useEffect, useState } from "react";

interface ResultadosPageProps {
    exibirImagemSorteio?: boolean;
}

export function ResultadosPage({ exibirImagemSorteio = true }: ResultadosPageProps) {
    const [sorteios, setSorteios] = useState<BuscarSorteioVigenteDto[]>([])
    const [sorteioSelecionado, setSorteioSelecionado] = useState<BuscarSorteioVigenteDto>()
    const [dadosPremiosDestaque, setDadosPremiosDestaque] = useState<PremioDto[]>([]);
    const [dadosPremiosGiro, setDadosPremiosGiro] = useState<PremioDto[]>([]);
    const [loading, setLoading] = useState(true)
    const [loadingPremios, setLoadingPremios] = useState(true)

    useEffect(() => {
        buscarTodosSorteios();
    }, [])

    async function buscarTodosSorteios() {
        const { Data: result } = await SorteioService.buscarTodosSorteios();
        setSorteios(result);
        setSorteioSelecionado(result[0]);
    }

    async function buscarPremiosPorSorteio() {
        try {
            setLoadingPremios(true);
            if (!sorteioSelecionado)
                return

            const result = await SorteioService.buscarPremiosPorSorteio(sorteioSelecionado.IdSorteio);

            setDadosPremiosDestaque(result.Data.Premios.filter((premio) => premio.Destaque === true));
            setDadosPremiosGiro(result.Data.Premios.filter((premio) => premio.Destaque === false));
        } catch (error) {

        } finally {
            setLoading(false);
            setLoadingPremios(false);
        }
    }

    function formatarDezenas(dezenas: string) {
        const dezenasFormatas: number[] = [];

        if(dezenas == null || dezenas == undefined) {
            return [];
        }

        for (let index = 0; index < dezenas.length; index += 2) {
            const dezena = dezenas.slice(index, index + 2);
            dezenasFormatas.push(Number(dezena))
        }

        return dezenasFormatas.map(dezena => `${dezena}`.padStart(2, '0'))
    }

    function formatarDescricaoPremio(descricao: string) {
        return descricao.replace(/^prêmio extra\s*/i, '');
    }

    useEffect(() => {
        if (!sorteioSelecionado) {
            return
        }

        buscarPremiosPorSorteio();

    }, [sorteioSelecionado])

    return (
        <>
            {
                loading ? (
                    <>
                        <div className="flex items-center mt-4 ">
                            <Skeleton className="h-80 bg-primary w-screen rounded-xl" />
                        </div>
                    </>
                ) : (
                    <div className="mx-2 my-5">
                        <div className="flex self-center items-center m-4 gap-2">
                            <span className="text-primary font-semibold text-2xl">Sorteio:</span>
                            <Select defaultValue={`${sorteioSelecionado?.IdSorteio}`} onValueChange={(v) => {
                                const sorteio = sorteios.find(s => `${s.IdSorteio}` === v)
                                setSorteioSelecionado(sorteio)
                            }}>
                                <SelectTrigger className="w-62 text-primary font-semibold text-2xl text-start">
                                    <SelectValue defaultValue={`${sorteioSelecionado?.IdSorteio}`} placeholder="Sorteio" />
                                </SelectTrigger>
                                <SelectContent defaultValue={`${sorteioSelecionado?.IdSorteio}`}>
                                    {
                                        sorteios.map(sorteio => {
                                            return import.meta.env.VITE_APP_THEME === 'MegaCap' ? <SelectItem key={sorteio.IdSorteio} value={`${sorteio.IdSorteio}`}>Edição - {sorteio?.DataFormatada}</SelectItem> : <SelectItem key={sorteio.IdSorteio} value={`${sorteio.IdSorteio}`}>{sorteio.Edicao}º Edição - {sorteio?.DataFormatada}</SelectItem>
                                        })
                                    }
                                </SelectContent>
                            </Select>
                        </div>

                        {
                            exibirImagemSorteio && (
                                <div className="mx-4 mb-4 flex flex-col justify-center items-center">
                                    <img src={sorteioSelecionado?.ImagemPremio} />
                                </div>
                            )
                        }

                        <div className="ms-4">
                            {
                                !loadingPremios && dadosPremiosDestaque?.map((premio) => {
                                    return (
                                        <div key={premio.IdPremio} className="my-4">
                                            <h3 className="my-2 px-4 py-2 rounded text-xl text-primary justify-center text-center">
                                                <Trophy className="inline mr-2" /> {premio.OrdemSorteio}º Prêmio{premio.PremioExtra ? ' Extra' : ''} - {formatarDescricaoPremio(premio.Descricao)}
                                            </h3>
                                            <div className="flex justify-center md:justify-start">
                                                <div className="flex flex-wrap justify-center select-none">
                                                    {
                                                        premio.Contemplados.length > 0 && formatarDezenas(premio.Contemplados[0].DezenasSorteadas).map((dezena, index) => {
                                                            return (
                                                                <span key={`${premio.Contemplados[0].DezenasSorteadas}-${index}`}
                                                                    className="flex items-center justify-center text-center tabular-nums rounded-full m-1 h-12 w-12 p-3 border-2 shadow-md">
                                                                    {dezena}
                                                                </span>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </div>

                                            <h3 className="my-2 px-4 py-2 rounded text-xl text-primary text-center">
                                                <Monitor className="inline mr-2" /> Ganhadores
                                            </h3>

                                            <div className="flex flex-col md:flex-row gap-2 justify-center">
                                                {
                                                    premio.Contemplados.map((contemplado) => {
                                                        return (
                                                            <Card key={contemplado.IdContemplado + "-ganhadores"} className="flex px-4 py-2 rounded min-w-28">
                                                                <div className="w-full">
                                                                    <p> <label className="font-bold">Ganhador:    </label> {contemplado.Nome}</p>
                                                                    <p> <label className="font-bold">Endereço:    </label> {contemplado.Endereco}</p>
                                                                    <p> <label className="font-bold">Nº da Sorte: </label> {contemplado.NumeroTitulo}</p>
                                                                    <p> <label className="font-bold">Vendedor:    </label> {contemplado.Vendedor}</p>
                                                                </div>
                                                                <div style={{ alignSelf: "center" }}>
                                                                    <img className="h-20 rounded-[50%]" src={contemplado.Imagem} />
                                                                </div>
                                                            </Card>
                                                        )
                                                    })
                                                }
                                            </div>

                                            <Separator className="my-4" />
                                        </div>
                                    )
                                })
                            }

                            {
                                loadingPremios && (
                                    <h3 className="my-2 px-4 py-2 rounded text-xl text-primary text-center">
                                        Carregando...
                                    </h3>
                                )
                            }

                            {
                                !loadingPremios && (
                                    <>
                                        <h3 className="flex flex-col gap-2 justify-center my-2 px-4 py-2 rounded text-xl text-primary text-center">
                                            <span> <Monitor style={{ display: "inline" }} /> Giro da Sorte</span>
                                            <span>
                                                {
                                                    dadosPremiosGiro[0]?.Descricao ?? ''
                                                }
                                            </span>
                                        </h3>
                                    </>
                                )
                            }

                            <div className="flex flex-wrap justify-center gap-3">
                                {
                                    !loadingPremios && dadosPremiosGiro?.map((premio) => {
                                        return (
                                            <Card key={premio.IdPremio} className={`relative w-full px-4 py-2 md:w-52 md:min-w-52${premio.PremioExtra ? " pt-7" : ""}`}>
                                                {premio.PremioExtra && (
                                                    <div
                                                        className="absolute top-2 right-2 z-10 rounded-md bg-primary px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-primary-foreground"
                                                        aria-label="Prêmio Extra"
                                                    >
                                                        Prêmio Extra
                                                    </div>
                                                )}
                                                {
                                                    premio.Contemplados.map((contemplado) => {
                                                        return (
                                                            <div key={contemplado.IdContemplado + "-giro-da-sorte"} className="flex">
                                                                <div className="w-full">
                                                                    <p className="font-bold">{contemplado.Nome}</p>
                                                                    <p className="text-sm">Endereço: {contemplado.Endereco}</p>
                                                                    <p className="text-sm">Nº da Sorte: {contemplado.NumeroTitulo}</p>
                                                                    <p className="text-sm">Vendedor: {contemplado.Vendedor}</p>
                                                                </div>
                                                                <div style={{ alignSelf: "center" }}>
                                                                    <img className="h-20 rounded-[50%]" src={contemplado.Imagem} />
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </Card>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    </div >
                )

            }

            <NavBar />
        </>
    )
}